/**
 * Device Capabilities Detection Service
 * Detects browser support for WASM SIMD, WebGPU, and memory requirements
 */

// WASM SIMD detection bytes
// This is a minimal WASM module that uses SIMD instructions
const WASM_SIMD_TEST_BYTES = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123,
  3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11
]);

/**
 * Device capabilities result
 */
export class DeviceCapabilities {
  constructor() {
    this.wasmSimd = false;
    this.webgpu = false;
    this.memory = 4; // GB
    this.isMobile = false;
    this.browser = '';
    this.browserVersion = 0;
    this.os = '';
    this.gpuInfo = null;
    this.checked = false;
    this.errors = [];
  }

  /**
   * Check if device meets requirements for audio AI
   * @param {Object} requirements - Required capabilities
   * @returns {Object} - { compatible: boolean, missing: string[] }
   */
  meetsRequirements(requirements = {}) {
    const missing = [];

    if (requirements.wasmSimd && !this.wasmSimd) {
      missing.push('WASM SIMD');
    }

    if (requirements.webgpu && !this.webgpu) {
      missing.push('WebGPU');
    }

    if (requirements.memory && this.memory < requirements.memory) {
      missing.push(`${requirements.memory}GB+ RAM`);
    }

    return {
      compatible: missing.length === 0,
      missing,
    };
  }
}

/**
 * Detect WASM SIMD support
 */
async function detectWasmSimd() {
  try {
    return WebAssembly.validate(WASM_SIMD_TEST_BYTES);
  } catch (e) {
    console.warn('[DeviceCapabilities] WASM SIMD detection failed:', e);
    return false;
  }
}

/**
 * Detect WebGPU support and get GPU info
 */
async function detectWebGPU() {
  if (!('gpu' in navigator)) {
    return { supported: false, info: null };
  }

  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      return { supported: false, info: null };
    }

    const info = await adapter.requestAdapterInfo?.() || {};

    return {
      supported: true,
      info: {
        vendor: info.vendor || 'unknown',
        architecture: info.architecture || 'unknown',
        device: info.device || 'unknown',
        description: info.description || 'unknown',
      },
    };
  } catch (e) {
    console.warn('[DeviceCapabilities] WebGPU detection failed:', e);
    return { supported: false, info: null };
  }
}

/**
 * Detect device memory
 */
function detectMemory() {
  // navigator.deviceMemory is available in Chrome/Edge/Opera
  // Returns approximate RAM in GB
  if ('deviceMemory' in navigator) {
    return navigator.deviceMemory;
  }
  // Fallback: assume 4GB
  return 4;
}

/**
 * Detect browser and version
 */
function detectBrowser() {
  const ua = navigator.userAgent;
  let browser = 'unknown';
  let version = 0;

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    version = match ? parseInt(match[1]) : 0;
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    version = match ? parseInt(match[1]) : 0;
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    version = match ? parseInt(match[1]) : 0;
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    version = match ? parseInt(match[1]) : 0;
  }

  return { browser, version };
}

/**
 * Detect operating system
 */
function detectOS() {
  const ua = navigator.userAgent;

  if (ua.includes('iPhone') || ua.includes('iPad')) {
    return 'iOS';
  }
  if (ua.includes('Android')) {
    return 'Android';
  }
  if (ua.includes('Mac OS')) {
    return 'macOS';
  }
  if (ua.includes('Windows')) {
    return 'Windows';
  }
  if (ua.includes('Linux')) {
    return 'Linux';
  }
  return 'unknown';
}

/**
 * Detect if mobile device
 */
function detectMobile() {
  return /Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
}

/**
 * Main detection function
 * @returns {Promise<DeviceCapabilities>}
 */
export async function detectCapabilities() {
  const capabilities = new DeviceCapabilities();

  try {
    // Parallel detection for speed
    const [wasmSimd, webgpuResult] = await Promise.all([
      detectWasmSimd(),
      detectWebGPU(),
    ]);

    capabilities.wasmSimd = wasmSimd;
    capabilities.webgpu = webgpuResult.supported;
    capabilities.gpuInfo = webgpuResult.info;
    capabilities.memory = detectMemory();
    capabilities.isMobile = detectMobile();

    const { browser, version } = detectBrowser();
    capabilities.browser = browser;
    capabilities.browserVersion = version;
    capabilities.os = detectOS();
    capabilities.checked = true;

    console.log('[DeviceCapabilities] Detection complete:', {
      wasmSimd: capabilities.wasmSimd,
      webgpu: capabilities.webgpu,
      memory: capabilities.memory,
      browser: `${capabilities.browser} ${capabilities.browserVersion}`,
      os: capabilities.os,
      mobile: capabilities.isMobile,
      gpu: capabilities.gpuInfo,
    });

  } catch (e) {
    console.error('[DeviceCapabilities] Detection error:', e);
    capabilities.errors.push(e.message);
  }

  return capabilities;
}

/**
 * Get minimum browser requirements for audio AI
 */
export function getMinimumRequirements() {
  return {
    wasmSimd: {
      Chrome: 91,
      Firefox: 89,
      Safari: 16.4,
      Edge: 91,
    },
    webgpu: {
      Chrome: 113,
      Firefox: null, // Not supported
      Safari: 17,
      Edge: 113,
    },
  };
}

/**
 * Check if browser version meets minimum for a feature
 */
export function checkBrowserSupport(browser, version, feature) {
  const requirements = getMinimumRequirements();
  const featureReqs = requirements[feature];

  if (!featureReqs) {
    return { supported: false, reason: 'Unknown feature' };
  }

  const minVersion = featureReqs[browser];

  if (minVersion === null) {
    return { supported: false, reason: `${browser} does not support ${feature}` };
  }

  if (minVersion === undefined) {
    return { supported: false, reason: `Unknown browser: ${browser}` };
  }

  if (version < minVersion) {
    return {
      supported: false,
      reason: `${browser} ${version} is too old. Minimum required: ${browser} ${minVersion}`
    };
  }

  return { supported: true };
}

// Singleton for caching
let cachedCapabilities = null;

/**
 * Get cached capabilities or detect new
 */
export async function getCapabilities() {
  if (cachedCapabilities) {
    return cachedCapabilities;
  }
  cachedCapabilities = await detectCapabilities();
  return cachedCapabilities;
}

/**
 * Clear cached capabilities (for testing)
 */
export function clearCapabilitiesCache() {
  cachedCapabilities = null;
}

export default {
  detectCapabilities,
  getCapabilities,
  getMinimumRequirements,
  checkBrowserSupport,
  DeviceCapabilities,
};
